import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, Ref } from "react";
import { createPortal, flushSync } from "react-dom";
import { Clock, Layers, MessageCircle, Tool } from "react-feather";
import "../styles/composer-tools.css";

export interface ComposerToolsProps {
  disabled?: boolean;
  contextKey?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  onWorkflows: () => void;
  onSchedules: () => void;
  onWhatsApp?: () => void;
}

export default function ComposerTools({ disabled = false, contextKey, triggerRef, onWorkflows, onSchedules, onWhatsApp }: ComposerToolsProps) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);
  const initialFocus = useRef(0);
  const menuId = useId();
  const setTrigger = useCallback((node: HTMLButtonElement | null) => {
    trigger.current = node;
    if (typeof triggerRef === "function") triggerRef(node);
    else if (triggerRef) triggerRef.current = node;
  }, [triggerRef]);
  const actions = [
    { label: "Workflows", Icon: Layers, run: onWorkflows },
    { label: "Scheduled Tasks", Icon: Clock, run: onSchedules },
    ...(onWhatsApp ? [{ label: "Import WhatsApp", Icon: MessageCircle, run: onWhatsApp }] : []),
  ];

  // A menu belongs to the chat where it was opened, not a later selection.
  useEffect(() => { setOpen(false); }, [contextKey, disabled]);

  const dismiss = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus && !trigger.current?.disabled) trigger.current?.focus({ preventScroll: true });
  };

  useLayoutEffect(() => {
    if (!open || disabled) return;
    const button = trigger.current, popup = menu.current;
    if (!button || !popup) return;
    const viewport = window.visualViewport;
    const position = () => {
      const bounds = button.getBoundingClientRect();
      const style = getComputedStyle(popup);
      const safe = (edge: string) => Math.max(8, parseFloat(style.getPropertyValue(`--ct-safe-${edge}`)) || 0);
      const leftEdge = (viewport?.offsetLeft ?? 0) + safe("left");
      const rightEdge = (viewport?.offsetLeft ?? 0) + (viewport?.width ?? window.innerWidth) - safe("right");
      const topEdge = (viewport?.offsetTop ?? 0) + safe("top");
      const bottomEdge = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight) - safe("bottom");
      const width = Math.max(0, Math.min(226, rightEdge - leftEdge));
      // Prefer above the pill; keep at least one scrollable row visible if
      // the keyboard/panning leaves almost no room above its anchor.
      const bottom = Math.min(bottomEdge, Math.max(topEdge + 60, bounds.top - 8));
      popup.style.width = `${width}px`;
      popup.style.maxHeight = `${Math.max(0, bottom - topEdge)}px`;
      popup.style.left = `${Math.max(leftEdge, Math.min(bounds.left, rightEdge - width))}px`;
      popup.style.top = `${Math.max(topEdge, bottom - popup.offsetHeight)}px`;
      popup.style.visibility = "visible";
    };
    position();
    items.current[initialFocus.current]?.focus({ preventScroll: true });
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    viewport?.addEventListener("resize", position);
    viewport?.addEventListener("scroll", position);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(position);
    observer?.observe(button);
    if (button.parentElement) observer?.observe(button.parentElement);
    const outsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !popup.contains(target) && !button.contains(target)) {
        setOpen(false);
        button.focus({ preventScroll: true });
      }
    };
    const outsideFocus = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Node && !popup.contains(target) && !button.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outsidePointer, true);
    document.addEventListener("focusin", outsideFocus);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      viewport?.removeEventListener("resize", position);
      viewport?.removeEventListener("scroll", position);
      observer?.disconnect();
      document.removeEventListener("pointerdown", outsidePointer, true);
      document.removeEventListener("focusin", outsideFocus);
      // No cleanup focus: an action may already have opened its own dialog.
    };
  }, [open, disabled, actions.length]);

  const openMenu = (last = false) => {
    if (disabled) return;
    initialFocus.current = last ? actions.length - 1 : 0;
    if (open) items.current[initialFocus.current]?.focus({ preventScroll: true });
    else setOpen(true);
  };
  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = items.current.findIndex(item => item === document.activeElement);
    let next: number | undefined;
    if (event.key === "ArrowDown") next = (current + 1) % actions.length;
    if (event.key === "ArrowUp") next = (current - 1 + actions.length) % actions.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = actions.length - 1;
    if (next !== undefined) {
      event.preventDefault(); event.stopPropagation(); items.current[next]?.focus({ preventScroll: true });
    } else if (event.key === "Escape") {
      event.preventDefault(); event.stopPropagation(); dismiss(true);
    } else if (event.key === "Tab") {
      // Return to the anchor before the browser's default Tab movement so
      // portal placement cannot jump navigation to the end of the document.
      dismiss(true);
    }
  };

  return <>
    <button ref={setTrigger} type="button" className="composer-tools__trigger" disabled={disabled}
      aria-label="Tools" aria-haspopup="menu" aria-expanded={open && !disabled} aria-controls={open && !disabled ? menuId : undefined}
      onClick={() => open ? dismiss(true) : openMenu()}
      onKeyDown={event => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault(); event.stopPropagation(); openMenu(event.key === "ArrowUp");
        } else if (event.key === "Escape" && open) {
          event.preventDefault(); event.stopPropagation(); dismiss(true);
        }
      }}>
      <Tool size={14} aria-hidden="true" /><span>Tools</span>
    </button>
    {open && !disabled && createPortal(
      <div ref={menu} id={menuId} className="composer-tools__menu" role="menu" aria-label="Composer tools" onKeyDown={onMenuKeyDown}>
        {actions.map(({ label, Icon, run }, index) => <button key={label} ref={node => { items.current[index] = node; }}
          type="button" role="menuitem" tabIndex={-1} className="composer-tools__item"
          onClick={() => {
            // Commit menu removal BEFORE handing focus/control to a dialog.
            flushSync(() => setOpen(false));
            run();
          }}>
          <Icon size={17} aria-hidden="true" /><span>{label}</span>
        </button>)}
      </div>, document.body,
    )}
  </>;
}
